import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../ui/Button";
import { ThemeToggle } from "../ui/ThemeToggle";
import { useAuth } from "../../context/AuthContext";
import { authFetch } from "../../lib/authFetch";
import FeedbackButton from "../feedback/FeedbackButton";
import NotificationBell, {
  NotificationItem,
} from "../ui/NotificationBell";

type DriverTopbarProps = {
  notifications?: NotificationItem[];
  unreadNotificationsCount?: number;
  onMarkNotificationsAsRead?: () => void;
  onClearNotifications?: () => void;
};

type CurrentUserResponse = {
  ok?: boolean;
  user?: {
    id?: number | string;
    username?: string;
    first_name?: string;
    last_name?: string;
    rol?: string;
  };
  error?: string;
};

const wrapperStyle: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 20,
  padding: "14px 20px 0 20px",
};

const barStyle: React.CSSProperties = {
  minHeight: "60px",
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "var(--surface)",
  boxShadow: "var(--shadow)",
  backdropFilter: "var(--glass)",
  WebkitBackdropFilter: "var(--glass)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "14px",
  padding: "10px 14px",
};

const leftStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  minWidth: 0,
};

const titleWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
};

const titleStyle: React.CSSProperties = {
  fontSize: "19px",
  fontWeight: 800,
  color: "var(--text-primary)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: 1.15,
};

const subtitleStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--text-secondary)",
  marginTop: "2px",
};

const rightStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexShrink: 0,
};

const compactButtonStyle: React.CSSProperties = {
  minHeight: "38px",
  minWidth: "38px",
  padding: "8px 10px",
  borderRadius: "14px",
};

const profileIconButtonStyle: React.CSSProperties = {
  minHeight: "38px",
  minWidth: "38px",
  padding: 0,
  borderRadius: "14px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "14px",
  fontWeight: 700,
  cursor: "pointer",
};

const safeString = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const getPageLabel = (pathname: string): string => {
  if (pathname === "/driver") return "Inicio";
  if (pathname.includes("/driver/deliveries")) return "Mis envíos";
  if (pathname.includes("/driver/pickups")) return "Mis colectas";
  if (pathname.includes("/driver/profile")) return "Mi perfil";
  return "Driver";
};

const DriverTopbar = ({
  notifications = [],
  unreadNotificationsCount = 0,
  onMarkNotificationsAsRead,
  onClearNotifications,
}: DriverTopbarProps) => {

  const navigate = useNavigate();
  const location = useLocation();
  const { token, userId, username, firstName, logout } = useAuth();

  const [resolvedFirstName, setResolvedFirstName] = useState<string>(
    safeString(firstName) || "Driver"
  );

  const pageLabel = useMemo(
    () => getPageLabel(location.pathname),
    [location.pathname]
  );

  useEffect(() => {
    let cancelled = false;

    const fetchCurrentUser = async () => {
      if (!token || !userId) {
        setResolvedFirstName(safeString(firstName) || "Driver");
        return;
      }

      try {
        const response = await authFetch(`/v2/users/${userId}`, { token });
        const payload = (await response
          .json()
          .catch(() => ({}))) as CurrentUserResponse;

        const resolvedName =
          safeString(payload?.user?.first_name) ||
          safeString(firstName) ||
          "Driver";

        if (!cancelled) {
          setResolvedFirstName(resolvedName);
        }
      } catch (error) {
        console.error(error);

        if (!cancelled) {
          setResolvedFirstName(safeString(firstName) || "Driver");
        }
      }
    };

    fetchCurrentUser();

    return () => {
      cancelled = true;
    };
  }, [token, userId, username, firstName]);

  return (
    <div style={wrapperStyle}>
      <div style={barStyle}>
        <div style={leftStyle}>
          <div style={titleWrapStyle}>
            <span style={titleStyle}>{`Hola, ${resolvedFirstName}`}</span>
            <span style={subtitleStyle}>{pageLabel}</span>
          </div>
        </div>

        <div style={rightStyle}>
          <FeedbackButton buttonStyle={profileIconButtonStyle} />
          <NotificationBell
            notifications={notifications}
            unreadCount={unreadNotificationsCount}
            onMarkAsRead={onMarkNotificationsAsRead}
            onClear={onClearNotifications}
          />
          <ThemeToggle />

          <button
            type="button"
            style={profileIconButtonStyle}
            onClick={() => navigate("/driver/profile")}
            title="Perfil"
            aria-label="Perfil"
          >
            ⚙
          </button>

          <Button variant="ghost" onClick={logout} style={compactButtonStyle}>
            Salir
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DriverTopbar;